import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { Button } from "primereact/button";
import { ADD_DEPARTMENT_URL, GET_DEPARTMENT_URL, EDIT_DEPARTMENT_URL } from '../../../utils/urls';

const DepartmentTab = ({ facilityId }) => {
  const [departmentName, setDepartmentName] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [isCustomDepartment, setIsCustomDepartment] = useState(false);
  const [fetchedDepartments, setFetchedDepartments] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editSelectedDepartment, setEditSelectedDepartment] = useState("");
  const [editIsCustomDepartment, setEditIsCustomDepartment] = useState(false);

  // Predefined departments
  const predefinedDepartments = [
    "Finance",
    "Property Management",
    "Operator"
  ];

  // Check if department name already exists
  const checkDuplicateName = (name, excludeId = null) => {
    if (!name || !name.trim()) return false;
    return fetchedDepartments.some(
      dept => dept.name.toLowerCase() === name.toLowerCase() &&
              (excludeId ? dept._id !== excludeId : true)
    );
  };

  // Get validation message for duplicate check
  const getDuplicateMessage = (name, excludeId = null) => {
    if (checkDuplicateName(name, excludeId)) {
      return `Department "${name}" already exists`;
    }
    return null;
  };

  const fetchDepartments = async () => {
    try {
      const response = await makeRequest2(
        `${GET_DEPARTMENT_URL}/${facilityId}`,
        "GET"
      );
      if (response.success && response.data) {
        const departmentData = response.data.data || response.data;
        const departmentsArray = Array.isArray(departmentData)
          ? departmentData
          : [departmentData];
        setFetchedDepartments(departmentsArray);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      toastify("Error fetching departments", "error");
    }
  };

  const handleDepartmentSelectionChange = (value) => {
    setSelectedDepartment(value);
    if (value === "other") {
      setIsCustomDepartment(true);
      setDepartmentName("");
    } else if (value !== "") {
      setIsCustomDepartment(false);
      setDepartmentName(value);
    } else {
      setIsCustomDepartment(false);
      setDepartmentName("");
    }
  };

  const handleDepartmentSubmit = async () => {
    const finalDepartmentName = isCustomDepartment ? departmentName.trim() : selectedDepartment;

    if (!finalDepartmentName) {
      toastify("Please select or enter a department name", "error");
      return;
    }

    // Check for duplicate department names (case-insensitive)
    const existingDepartment = fetchedDepartments.find(
      dept => dept.name.toLowerCase() === finalDepartmentName.toLowerCase()
    );

    if (existingDepartment) {
      toastify(`Department "${finalDepartmentName}" already exists`, "error");
      return;
    }

    try {
      const data = {
        name: finalDepartmentName
      };

      const response = await makeRequest2(
        `${ADD_DEPARTMENT_URL}/${facilityId}`,
        "POST",
        data
      );

      if (response?.success === true || response?.message === "Department added successfully") {
        toastify("Department added successfully", "success");
        await fetchDepartments();
        resetForm();
      } else {
        const errorMessage = response?.message || response?.error || "Operation failed";
        toastify(errorMessage, "error");
        await fetchDepartments();
      }
    } catch (error) {
      console.error("Submit error:", error);
      toastify(error.message || "Error submitting department", "error");
      await fetchDepartments();
    }
  };

  const resetForm = () => {
    setDepartmentName("");
    setSelectedDepartment("");
    setIsCustomDepartment(false);
  };

  const handleEditDepartmentSelectionChange = (value) => {
    setEditSelectedDepartment(value);
    if (value === "other") {
      setEditIsCustomDepartment(true);
      setEditingDepartment({
        ...editingDepartment,
        name: ""
      });
    } else if (value !== "") {
      setEditIsCustomDepartment(false);
      setEditingDepartment({
        ...editingDepartment,
        name: value
      });
    } else {
      setEditIsCustomDepartment(false);
      setEditingDepartment({
        ...editingDepartment,
        name: ""
      });
    }
  };

  const handleEditSubmit = async () => {
    if (editingDepartment == null) return;

    const finalEditName = editIsCustomDepartment
      ? editingDepartment.name.trim()
      : editSelectedDepartment;

    if (!finalEditName) {
      toastify("Please select or enter a department name", "error");
      return;
    }

    // Check for duplicate department names (case-insensitive), excluding the current department being edited
    const existingDepartment = fetchedDepartments.find(
      dept => dept.name.toLowerCase() === finalEditName.toLowerCase() &&
              dept._id !== editingDepartment._id
    );

    if (existingDepartment) {
      toastify(`Department "${finalEditName}" already exists`, "error");
      return;
    }

    try {
      const data = {
        name: finalEditName
      };

      const response = await makeRequest2(
        `${EDIT_DEPARTMENT_URL}/${facilityId}/${editingDepartment._id}`,
        "PUT",
        data
      );
      
      if (response.success || response?.message === "Department updated successfully") {
        toastify("Department updated successfully", "success");
        await fetchDepartments();
        setShowEditModal(false);
        setEditingDepartment(null);
        setEditSelectedDepartment("");
        setEditIsCustomDepartment(false);
      } else {
        toastify(response.message || "Failed to update department", "error");
      }
    } catch (error) {
      console.error("Edit error:", error);
      toastify("Error updating department", "error");
    }
  };

  useEffect(() => {
    if (facilityId) {
      fetchDepartments();
    }
  }, [facilityId]);

  return (
    <div className="row p-3">
      {/* Add Department Section */}
      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <h5>Add Department</h5>

            <div className="mt-2">
              <label>Department Name:</label>
              <select
                className={`form-control mt-2 ${
                  selectedDepartment && selectedDepartment !== "other" && checkDuplicateName(selectedDepartment) ? 'is-invalid' : ''
                }`}
                value={selectedDepartment}
                onChange={(e) => handleDepartmentSelectionChange(e.target.value)}
              >
                <option value="">Select a department</option>
                {predefinedDepartments.map((dept) => (
                  <option key={dept} value={dept} disabled={checkDuplicateName(dept)}>
                    {dept} {checkDuplicateName(dept) ? '(Already exists)' : ''}
                  </option>
                ))}
                <option value="other">Other (Custom Department)</option>
              </select>
              {selectedDepartment && selectedDepartment !== "other" && checkDuplicateName(selectedDepartment) && (
                <div className="invalid-feedback">
                  {getDuplicateMessage(selectedDepartment)}
                </div>
              )}

              {isCustomDepartment && (
                <div className="mt-2">
                  <input
                    required
                    className={`form-control ${
                      departmentName && checkDuplicateName(departmentName) ? 'is-invalid' : ''
                    }`}
                    type="text"
                    value={departmentName}
                    onChange={(e) => setDepartmentName(e.target.value)}
                    placeholder="Enter custom department name"
                    maxLength={100}
                  />
                  {departmentName && checkDuplicateName(departmentName) && (
                    <div className="invalid-feedback">
                      {getDuplicateMessage(departmentName)}
                    </div>
                  )}
                </div>
              )}

              <small className="text-muted">
                {isCustomDepartment
                  ? "Enter a unique name for the custom department"
                  : "Select from predefined departments or choose 'Other' for custom entry"
                }
              </small>
            </div>

            <div className="mt-4" style={{ textAlign: "right" }}>
              <button
                className="btn btn-primary"
                onClick={handleDepartmentSubmit}
                disabled={
                  isCustomDepartment
                    ? !departmentName.trim() || checkDuplicateName(departmentName)
                    : !selectedDepartment || selectedDepartment === "other" || checkDuplicateName(selectedDepartment)
                }
              >
                Add Department
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Current Departments Section */}
      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <h5 className="mb-4">Current Departments</h5>
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th scope="col">DEPARTMENT NAME</th>
                    <th scope="col">CREATED</th>
                    <th scope="col" className="text-end">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {fetchedDepartments.length > 0 ? (
                    fetchedDepartments.map((department) => (
                      <tr key={department._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="me-2">
                              <i className="ti ti-building text-primary" style={{ fontSize: "18px" }}></i>
                            </div>
                            <div>
                              <strong>{department.name}</strong>
                            </div>
                          </div>
                        </td>
                        <td>
                          {department.createdAt ? (
                            new Date(department.createdAt).toLocaleDateString()
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="text-end">
                          <Button
                            icon="ti ti-pencil"
                            className="p-button-rounded p-button-info"
                            onClick={() => {
                              setEditingDepartment({...department});
                              // Check if current department is in predefined list
                              if (predefinedDepartments.includes(department.name)) {
                                setEditSelectedDepartment(department.name);
                                setEditIsCustomDepartment(false);
                              } else {
                                setEditSelectedDepartment("other");
                                setEditIsCustomDepartment(true);
                              }
                              setShowEditModal(true);
                            }}
                            tooltip="Edit Department"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4">
                        <div className="text-muted">
                          <i className="ti ti-building-warehouse mb-2" style={{ fontSize: "24px" }}></i>
                          <p className="mb-0">No departments available</p>
                          <small>Add your first department to get started</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Department Modal */}
      {showEditModal && editingDepartment && (
        <>
          <div className="modal show fade" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="ti ti-pencil me-2"></i>
                    Edit Department
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingDepartment(null);
                      setEditSelectedDepartment("");
                      setEditIsCustomDepartment(false);
                    }}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Department Name</label>
                    <select
                      className={`form-control ${
                        editSelectedDepartment && editSelectedDepartment !== "other" &&
                        checkDuplicateName(editSelectedDepartment, editingDepartment._id) ? 'is-invalid' : ''
                      }`}
                      value={editSelectedDepartment}
                      onChange={(e) => handleEditDepartmentSelectionChange(e.target.value)}
                    >
                      <option value="">Select a department</option>
                      {predefinedDepartments.map((dept) => (
                        <option
                          key={dept}
                          value={dept}
                          disabled={checkDuplicateName(dept, editingDepartment._id)}
                        >
                          {dept} {checkDuplicateName(dept, editingDepartment._id) ? '(Already exists)' : ''}
                        </option>
                      ))}
                      <option value="other">Other (Custom Department)</option>
                    </select>
                    {editSelectedDepartment && editSelectedDepartment !== "other" &&
                     checkDuplicateName(editSelectedDepartment, editingDepartment._id) && (
                      <div className="invalid-feedback">
                        {getDuplicateMessage(editSelectedDepartment, editingDepartment._id)}
                      </div>
                    )}

                    {editIsCustomDepartment && (
                      <div className="mt-2">
                        <input
                          type="text"
                          className={`form-control ${
                            editingDepartment.name &&
                            checkDuplicateName(editingDepartment.name, editingDepartment._id) ? 'is-invalid' : ''
                          }`}
                          value={editingDepartment.name || ""}
                          onChange={(e) =>
                            setEditingDepartment({
                              ...editingDepartment,
                              name: e.target.value
                            })
                          }
                          placeholder="Enter custom department name"
                          maxLength={100}
                        />
                        {editingDepartment.name &&
                         checkDuplicateName(editingDepartment.name, editingDepartment._id) && (
                          <div className="invalid-feedback">
                            {getDuplicateMessage(editingDepartment.name, editingDepartment._id)}
                          </div>
                        )}
                      </div>
                    )}

                    <small className="text-muted">
                      {editIsCustomDepartment
                        ? "Enter a unique name for the custom department"
                        : "Select from predefined departments or choose 'Other' for custom entry"
                      }
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingDepartment(null);
                      setEditSelectedDepartment("");
                      setEditIsCustomDepartment(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEditSubmit}
                    disabled={
                      editIsCustomDepartment
                        ? !editingDepartment.name?.trim() ||
                          checkDuplicateName(editingDepartment.name, editingDepartment._id)
                        : !editSelectedDepartment ||
                          editSelectedDepartment === "other" ||
                          checkDuplicateName(editSelectedDepartment, editingDepartment._id)
                    }
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Backdrop */}
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default DepartmentTab;